import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Check, FileText, Paperclip, X } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import useSEO from "@/lib/useSEO";
import { Reveal } from "@/components/motion/Reveal";

const eur = (n) => n == null ? "—" : new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(n);

const STEPS = ["Servizio", "Progetto", "Riepilogo", "I tuoi dati", "La tua stima"];

const PROPERTY_TYPES = ["Appartamento", "Villa", "Attico", "Loft", "Ufficio", "Negozio", "Altro"];
const PROPERTY_STATES = ["Da ristrutturare", "In buono stato", "Nuovo / in costruzione", "Da completare"];
const INTERVENTIONS = ["Progettazione completa", "Ridistribuzione spazi", "Solo arredi e finiture", "Consulenza mirata"];
const GOALS = ["Abitare", "Vendere", "Mettere a reddito", "Ufficio / lavoro"];
const TIMELINES = ["Il prima possibile", "Entro 3 mesi", "Entro 6 mesi", "Oltre 6 mesi", "Sto solo valutando"];
const BUDGETS = ["Fino a 5.000 €", "5.000 – 15.000 €", "15.000 – 50.000 €", "Oltre 50.000 €", "Preferisco non indicare"];
const VISIT_MODES = [
  { v: "online", l: "Online", d: "Nessun costo di trasferta" },
  { v: "studio", l: "Presso lo studio", d: "Nessun costo di trasferta" },
  { v: "immobile", l: "Presso l'immobile", d: "Trasferta secondo fascia chilometrica" },
];
const TRAVEL_BANDS = [
  { v: "0-20", l: "Entro 20 km dallo studio" },
  { v: "21-50", l: "Da 21 a 50 km" },
  { v: "51-100", l: "Da 51 a 100 km" },
  { v: "100+", l: "Oltre 100 km" },
];

const emptyForm = {
  package_key: "", property_type: "", sqm: "", comune: "", cap: "", address: "",
  scope: "", rooms: "", property_state: "", intervention_type: "", goal: "",
  visit_mode: "online", travel_band: "none", urgency: false, timeline: "",
  budget_band: "", message: "", attachment_url: "",
  name: "", surname: "", email: "", phone: "", company: "", contact_preference: "email",
  privacy_consent: false, newsletter_consent: false, website: "",
};

function getClientToken() {
  let t = sessionStorage.getItem("est_client_token");
  if (!t) { t = crypto.randomUUID(); sessionStorage.setItem("est_client_token", t); }
  return t;
}

function F({ label, children, testId, optional }) {
  return (
    <div data-testid={testId}>
      <Label className="eyebrow !tracking-[0.14em]">{label}{optional && <span className="normal-case tracking-normal text-taupe"> (facoltativo)</span>}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function ChoiceGrid({ options, value, onChange, testId, cols = "sm:grid-cols-3" }) {
  return (
    <div className={`grid grid-cols-1 ${cols} gap-2`} data-testid={testId}>
      {options.map((o) => {
        const v = o.v ?? o;
        const l = o.l ?? o;
        const active = value === v;
        return (
          <button key={v} type="button" onClick={() => onChange(v)} data-testid={`${testId}-${v}`}
            className={`min-h-[44px] px-4 py-3 text-left text-sm border transition-colors ${active ? "bg-ink text-bone border-ink" : "bg-white border-hair hover:border-ink"}`}>
            <span className="block">{l}</span>
            {o.d && <span className={`block text-xs mt-0.5 ${active ? "text-bone/70" : "text-taupe"}`}>{o.d}</span>}
          </button>
        );
      })}
    </div>
  );
}

export default function Stima() {
  useSEO({ title: "Calcola la tua stima gratuita", description: "Scopri quanto può costare il tuo progetto: ricevi gratuitamente una stima preliminare personalizzata, calcolata sulle tariffe reali dello Studio Giovanna Matino Interior." });
  const [cfg, setCfg] = useState(null);
  const [loadErr, setLoadErr] = useState(false);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(() => {
    try { return { ...emptyForm, ...JSON.parse(sessionStorage.getItem("est_form") || "{}") }; }
    catch { return emptyForm; }
  });
  const [uploadBusy, setUploadBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/estimator/config").then((r) => setCfg(r.data)).catch(() => setLoadErr(true));
  }, []);

  useEffect(() => { sessionStorage.setItem("est_form", JSON.stringify(form)); }, [form]);

  const set = (k) => (e) => {
    const v = e?.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e;
    setForm((p) => ({ ...p, [k]: v }));
  };

  const pkg = useMemo(() => cfg?.packages?.find((p) => p.key === form.package_key), [cfg, form.package_key]);
  const isFlat = pkg?.pricing === "flat";

  const sqmValid = useMemo(() => {
    if (isFlat) return true;
    const n = parseInt(form.sqm, 10);
    return Number.isInteger(n) && n >= 1 && n <= 1000 && String(n) === String(form.sqm).trim();
  }, [form.sqm, isFlat]);

  const step1Valid = form.package_key && sqmValid && form.property_type && form.comune.trim()
    && /^[0-9]{5}$/.test(form.cap.trim())
    && (form.visit_mode !== "immobile" || (form.travel_band && form.travel_band !== "none"));

  const goTo = (s) => {
    setStep(s);
    window.scrollTo(0, 0);
  };

  const uploadFile = async (file) => {
    if (!file) return;
    const ext = file.name.rsplit ? null : null;
    const ok = ["pdf", "jpg", "jpeg", "png"].includes((file.name.split(".").pop() || "").toLowerCase());
    if (!ok) { toast.error("Formato non ammesso. Accettati: PDF, JPG, PNG."); return; }
    setUploadBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/estimator/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((p) => ({ ...p, attachment_url: data.url }));
      toast.success("Planimetria allegata");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore caricamento");
    } finally { setUploadBusy(false); }
  };

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/estimator/submit", {
        ...form,
        sqm: isFlat ? null : parseInt(form.sqm, 10),
        travel_band: form.visit_mode === "immobile" ? form.travel_band : "none",
        client_token: getClientToken(),
      });
      setResult(data);
      sessionStorage.removeItem("est_form");
      setStep(4);
      window.scrollTo(0, 0);
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Errore nell'invio, riprova");
    } finally { setBusy(false); }
  };

  if (loadErr) return <div className="container-x py-24 text-taupe" data-testid="stima-error">Errore di caricamento. Riprova tra poco.</div>;
  if (!cfg) return <div className="container-x py-24 text-taupe">Caricamento…</div>;

  if (!cfg.available) {
    return (
      <div className="container-x py-24 max-w-2xl" data-testid="stima-unavailable">
        <span className="eyebrow">Calcola la tua stima</span>
        <h1 className="font-serif text-4xl md:text-5xl mt-4">Il configuratore sarà disponibile a breve.</h1>
        <p className="mt-6 text-taupe leading-relaxed">Lo Studio sta completando la configurazione delle condizioni fiscali. Nel frattempo puoi prenotare una consulenza conoscitiva gratuita.</p>
        <Link to="/prenota" className="btn-ink mt-8 inline-flex">Prenota una consulenza</Link>
      </div>
    );
  }

  return (
    <div data-testid="page-stima">
      <section className="container-x pt-10 pb-8 max-w-3xl">
        <span className="eyebrow">Calcola la tua stima gratuita</span>
        <h1 className="font-serif text-4xl md:text-6xl leading-[1.05] mt-4">Scopri quanto può costare il tuo progetto.</h1>
        <p className="mt-5 text-taupe leading-relaxed font-light">
          Ricevi <strong className="text-ink font-normal">gratuitamente</strong> una stima preliminare personalizzata, calcolata sulle tariffe reali dello Studio.
          Gli importi rappresentano il costo stimato <strong className="text-ink font-normal">del servizio professionale</strong>: la stima è sempre gratuita e senza impegno.
        </p>
        {/* Progress */}
        <div className="mt-8 flex gap-1.5" data-testid="stima-progress">
          {STEPS.map((s, i) => (
            <div key={s} className="flex-1">
              <div className={`h-1 transition-colors ${i <= step ? "bg-gold" : "bg-hair"}`} />
              <div className={`mt-2 text-[10px] tracking-[0.12em] uppercase ${i === step ? "text-ink" : "text-taupe"} ${i > step ? "hidden sm:block" : ""}`}>{s}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="container-x pb-24 max-w-3xl">
        {/* STEP 0 — Servizio */}
        {step === 0 && (
          <Reveal>
            <div className="space-y-4" data-testid="stima-step-service">
              {cfg.packages.map((p) => (
                <div key={p.key} className={`bg-white border p-6 transition-colors ${form.package_key === p.key ? "border-gold" : "border-hair"}`} data-testid={`stima-pkg-${p.key}`}>
                  <div className="flex justify-between items-start gap-4 flex-wrap">
                    <div>
                      <h3 className="font-serif text-2xl">{p.name}</h3>
                      <p className="text-sm text-taupe mt-2 leading-relaxed">{p.description}</p>
                    </div>
                    <button onClick={() => set("package_key")(p.key)} data-testid={`stima-select-${p.key}`}
                      className={`min-h-[44px] px-6 text-xs tracking-[0.14em] uppercase border transition-colors w-full sm:w-auto ${form.package_key === p.key ? "bg-ink text-bone border-ink" : "border-ink hover:bg-ink hover:text-bone"}`}>
                      {form.package_key === p.key ? "Selezionato ✓" : "Seleziona"}
                    </button>
                  </div>
                  {(p.included?.length > 0 || p.excluded?.length > 0) && (
                    <div className="grid sm:grid-cols-2 gap-6 mt-5 pt-5 border-t border-hair text-sm">
                      <div>
                        <div className="eyebrow !text-[10px] mb-2">Comprende</div>
                        <ul className="space-y-1.5 text-taupe">{(p.included || []).slice(0, 5).map((i) => <li key={i} className="flex gap-2"><Check size={14} className="text-gold shrink-0 mt-0.5" />{i}</li>)}</ul>
                      </div>
                      <div>
                        <div className="eyebrow !text-[10px] mb-2">Non comprende</div>
                        <ul className="space-y-1.5 text-taupe">{(p.excluded || []).slice(0, 5).map((i) => <li key={i} className="flex gap-2"><X size={14} className="text-taupe shrink-0 mt-0.5" />{i}</li>)}</ul>
                      </div>
                    </div>
                  )}
                  {p.timing && <div className="mt-4 text-xs text-taupe">Tempi indicativi: {p.timing}</div>}
                </div>
              ))}
              <div className="pt-4 flex justify-end">
                <button disabled={!form.package_key} onClick={() => goTo(1)} className="btn-ink w-full sm:w-auto min-h-[48px] disabled:opacity-40" data-testid="stima-next-0">Continua</button>
              </div>
            </div>
          </Reveal>
        )}

        {/* STEP 1 — Progetto */}
        {step === 1 && (
          <div className="space-y-8" data-testid="stima-step-project">
            {!isFlat && (
              <F label="Superficie dell'immobile (m²)" testId="stima-f-sqm">
                <Input type="number" inputMode="numeric" min={1} max={1000} step={1} value={form.sqm}
                  onChange={(e) => set("sqm")(e.target.value.replace(/[^0-9]/g, ""))}
                  placeholder="es. 90" className="rounded-none" data-testid="stima-sqm" />
                {form.sqm && !sqmValid && <p className="text-xs text-red-700 mt-2" data-testid="stima-sqm-error">Inserisci un numero intero tra 1 e 1.000 m².</p>}
                {sqmValid && parseInt(form.sqm) > 200 && <p className="text-xs text-gold mt-2" data-testid="stima-sqm-manual">Oltre 200 m²: la stima sarà elaborata su misura dallo Studio.</p>}
              </F>
            )}
            <F label="Tipologia di immobile" testId="stima-f-ptype">
              <ChoiceGrid options={PROPERTY_TYPES} value={form.property_type} onChange={set("property_type")} testId="stima-ptype" />
            </F>
            <div className="grid sm:grid-cols-2 gap-5">
              <F label="Comune" testId="stima-f-comune"><Input value={form.comune} onChange={set("comune")} className="rounded-none" data-testid="stima-comune" /></F>
              <F label="CAP" testId="stima-f-cap"><Input inputMode="numeric" maxLength={5} value={form.cap} onChange={(e) => set("cap")(e.target.value.replace(/\D/g, ""))} className="rounded-none" data-testid="stima-cap" /></F>
            </div>
            {form.cap && !/^[0-9]{5}$/.test(form.cap.trim()) && <p className="text-xs text-red-700 -mt-4" data-testid="stima-cap-error">Il CAP deve essere di 5 cifre.</p>}
            <F label="Indirizzo dell'immobile" optional={form.visit_mode !== "immobile"} testId="stima-f-address">
              <Input value={form.address} onChange={set("address")} className="rounded-none" data-testid="stima-address" />
            </F>
            <div className="grid sm:grid-cols-2 gap-5">
              <F label="Immobile intero o singolo ambiente" testId="stima-f-scope">
                <ChoiceGrid cols="sm:grid-cols-2" options={[{ v: "intero", l: "Immobile intero" }, { v: "singolo", l: "Singolo ambiente" }]} value={form.scope} onChange={set("scope")} testId="stima-scope" />
              </F>
              <F label="Numero degli ambienti" optional testId="stima-f-rooms">
                <Input inputMode="numeric" value={form.rooms} onChange={(e) => set("rooms")(e.target.value.replace(/\D/g, ""))} className="rounded-none" data-testid="stima-rooms" />
              </F>
            </div>
            <F label="Stato dell'immobile" testId="stima-f-state">
              <ChoiceGrid cols="sm:grid-cols-2" options={PROPERTY_STATES} value={form.property_state} onChange={set("property_state")} testId="stima-state" />
            </F>
            <F label="Tipo di intervento" testId="stima-f-intervention">
              <ChoiceGrid cols="sm:grid-cols-2" options={INTERVENTIONS} value={form.intervention_type} onChange={set("intervention_type")} testId="stima-intervention" />
            </F>
            <F label="Obiettivo principale" testId="stima-f-goal">
              <ChoiceGrid cols="sm:grid-cols-2" options={GOALS} value={form.goal} onChange={set("goal")} testId="stima-goal" />
            </F>
            <F label="Dove si svolgerà il servizio?" testId="stima-f-visit">
              <ChoiceGrid options={VISIT_MODES} value={form.visit_mode} onChange={(v) => { set("visit_mode")(v); if (v !== "immobile") set("travel_band")("none"); }} testId="stima-visit" />
            </F>
            {form.visit_mode === "immobile" && (
              <F label="Fascia chilometrica dichiarata (verificata dallo Studio)" testId="stima-f-travel">
                <ChoiceGrid cols="sm:grid-cols-2" options={TRAVEL_BANDS} value={form.travel_band} onChange={set("travel_band")} testId="stima-travel" />
                <p className="text-xs text-taupe mt-2">{cfg.travel?.verify_note} {cfg.travel?.excluded_note}</p>
              </F>
            )}
            <F label="Hai necessità di ricevere il servizio in tempi inferiori a quelli ordinari?" testId="stima-f-urgency">
              <ChoiceGrid cols="sm:grid-cols-2" options={[{ v: "no", l: "No" }, { v: "si", l: "Sì", d: `Maggiorazione del ${cfg.urgency_percent}% — subordinata alla disponibilità dello Studio` }]}
                value={form.urgency ? "si" : "no"} onChange={(v) => set("urgency")(v === "si")} testId="stima-urgency" />
            </F>
            <div className="grid sm:grid-cols-2 gap-5">
              <F label="Tempistica prevista" testId="stima-f-timeline">
                <Select value={form.timeline} onValueChange={set("timeline")}>
                  <SelectTrigger className="rounded-none" data-testid="stima-timeline"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                  <SelectContent>{TIMELINES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </F>
              <F label="Fascia di budget complessiva" optional testId="stima-f-budget">
                <Select value={form.budget_band} onValueChange={set("budget_band")}>
                  <SelectTrigger className="rounded-none" data-testid="stima-budget"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                  <SelectContent>{BUDGETS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </F>
            </div>
            <F label="Messaggio o esigenze particolari" optional testId="stima-f-message">
              <Textarea rows={3} value={form.message} onChange={set("message")} className="rounded-none" data-testid="stima-message" />
            </F>
            <F label={`Planimetria (PDF, JPG o PNG — max ${cfg.max_attachment_mb} MB)`} optional testId="stima-f-attachment">
              <div className="flex items-center gap-3 flex-wrap">
                <label className="min-h-[44px] inline-flex items-center gap-2 text-xs tracking-[0.12em] uppercase border border-ink px-4 py-2 cursor-pointer hover:bg-ink hover:text-bone transition-colors" data-testid="stima-attachment-btn">
                  <Paperclip size={14} /> {uploadBusy ? "Caricamento…" : "Allega file"}
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" data-testid="stima-attachment-input"
                    onChange={(e) => { uploadFile(e.target.files?.[0]); e.target.value = ""; }} />
                </label>
                {form.attachment_url && (
                  <span className="text-xs text-emerald-700 inline-flex items-center gap-2" data-testid="stima-attachment-ok">
                    <Check size={14} /> Allegata
                    <button type="button" onClick={() => set("attachment_url")("")} className="text-red-700 underline" data-testid="stima-attachment-remove">rimuovi</button>
                  </span>
                )}
              </div>
            </F>
            <div className="pt-4 flex gap-3 flex-col sm:flex-row">
              <button onClick={() => goTo(0)} className="min-h-[48px] px-6 text-xs tracking-[0.14em] uppercase border border-hair hover:border-ink transition-colors w-full sm:w-auto inline-flex items-center justify-center gap-2" data-testid="stima-back-1"><ArrowLeft size={14} /> Indietro</button>
              <button disabled={!step1Valid} onClick={() => goTo(2)} className="btn-ink w-full sm:flex-1 min-h-[48px] disabled:opacity-40" data-testid="stima-next-1">Vai al riepilogo</button>
            </div>
          </div>
        )}

        {/* STEP 2 — Riepilogo (senza importi: la stima si sblocca solo dopo i dati e il consenso) */}
        {step === 2 && (
          <div className="space-y-6" data-testid="stima-step-summary">
            <div className="bg-white border border-hair p-6 md:p-8">
              <div className="eyebrow mb-5">Riepilogo delle tue scelte</div>
              <dl className="space-y-2 text-sm">
                {[
                  ["Servizio", pkg?.name],
                  !isFlat && ["Superficie", `${form.sqm} m²`],
                  ["Tipologia", form.property_type],
                  ["Località", `${form.comune} (${form.cap})`],
                  ["Modalità", VISIT_MODES.find((m) => m.v === form.visit_mode)?.l],
                  form.visit_mode === "immobile" && ["Fascia trasferta", TRAVEL_BANDS.find((t) => t.v === form.travel_band)?.l],
                  ["Urgenza", form.urgency ? "Sì — subordinata alla disponibilità dello Studio" : "No"],
                ].filter(Boolean).map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-4 border-b border-hair/60 pb-2"><dt className="text-taupe">{k}</dt><dd className="text-right">{v}</dd></div>
                ))}
              </dl>
            </div>

            {(pkg?.included?.length > 0 || pkg?.excluded?.length > 0) && (
              <div className="bg-white border border-hair p-6 md:p-8 grid sm:grid-cols-2 gap-6 text-sm">
                <div>
                  <div className="eyebrow !text-[10px] mb-2">Principali inclusioni</div>
                  <ul className="space-y-1.5 text-taupe">{(pkg.included || []).slice(0, 6).map((i) => <li key={i} className="flex gap-2"><Check size={14} className="text-gold shrink-0 mt-0.5" />{i}</li>)}</ul>
                </div>
                <div>
                  <div className="eyebrow !text-[10px] mb-2">Principali esclusioni</div>
                  <ul className="space-y-1.5 text-taupe">{(pkg.excluded || []).slice(0, 6).map((i) => <li key={i} className="flex gap-2"><X size={14} className="text-taupe shrink-0 mt-0.5" />{i}</li>)}</ul>
                </div>
              </div>
            )}

            <div className="border border-gold/40 bg-gold/5 p-5 text-sm text-taupe leading-relaxed" data-testid="stima-price-gate">
              <strong className="text-ink">La tua stima personalizzata è quasi pronta.</strong><br />
              Per tutelare i tuoi dati, l'importo viene calcolato e mostrato solo dopo l'inserimento dei tuoi
              recapiti e l'accettazione dell'informativa privacy. La riceverai anche via email in PDF.
            </div>

            <div className="pt-2 flex gap-3 flex-col sm:flex-row">
              <button onClick={() => goTo(1)} className="min-h-[48px] px-6 text-xs tracking-[0.14em] uppercase border border-hair hover:border-ink transition-colors w-full sm:w-auto inline-flex items-center justify-center gap-2" data-testid="stima-back-2"><ArrowLeft size={14} /> Correggi i dati</button>
              <button onClick={() => goTo(3)} className="btn-ink w-full sm:flex-1 min-h-[48px]" data-testid="stima-next-2">Inserisci i tuoi dati e scopri la stima</button>
            </div>
          </div>
        )}

        {/* STEP 3 — Dati cliente */}
        {step === 3 && (
          <div className="space-y-6" data-testid="stima-step-client">
            <div className="grid sm:grid-cols-2 gap-5">
              <F label="Nome *" testId="stima-f-name"><Input value={form.name} onChange={set("name")} className="rounded-none" data-testid="stima-name" /></F>
              <F label="Cognome *" testId="stima-f-surname"><Input value={form.surname} onChange={set("surname")} className="rounded-none" data-testid="stima-surname" /></F>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <F label="Email *" testId="stima-f-email"><Input type="email" value={form.email} onChange={set("email")} className="rounded-none" data-testid="stima-email" /></F>
              <F label="Telefono *" testId="stima-f-phone"><Input value={form.phone} onChange={set("phone")} className="rounded-none" data-testid="stima-phone" /></F>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <F label="Azienda" optional testId="stima-f-company"><Input value={form.company} onChange={set("company")} className="rounded-none" data-testid="stima-company" /></F>
              <F label="Preferenza di contatto" testId="stima-f-contact">
                <ChoiceGrid cols="sm:grid-cols-3" options={["email", "telefono", "whatsapp"]} value={form.contact_preference} onChange={set("contact_preference")} testId="stima-contact" />
              </F>
            </div>
            {/* honeypot */}
            <div aria-hidden="true" style={{ position: "absolute", left: "-9999px" }}>
              <input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} data-testid="stima-website" />
            </div>
            <label className="flex items-start gap-3 text-xs text-taupe leading-relaxed cursor-pointer bg-white border border-hair p-4 min-h-[44px]">
              <input type="checkbox" checked={form.privacy_consent} onChange={set("privacy_consent")} className="mt-0.5 w-5 h-5 shrink-0 accent-[#9E8462]" data-testid="stima-privacy" />
              <span>Dichiaro di aver letto l'<Link to="/privacy" target="_blank" className="underline hover:text-gold">informativa privacy</Link> e acconsento al trattamento dei miei dati per la gestione della richiesta e l'elaborazione della stima. <span className="text-ink">*</span></span>
            </label>
            <label className="flex items-start gap-3 text-xs text-taupe leading-relaxed cursor-pointer bg-white border border-hair p-4 min-h-[44px]">
              <input type="checkbox" checked={form.newsletter_consent} onChange={set("newsletter_consent")} className="mt-0.5 w-5 h-5 shrink-0 accent-[#9E8462]" data-testid="stima-newsletter" />
              <span>Desidero ricevere aggiornamenti, novità e comunicazioni commerciali da Giovanna Matino Interior. (facoltativo — la stima ti arriva comunque)</span>
            </label>
            <div className="pt-2 flex gap-3 flex-col sm:flex-row">
              <button onClick={() => goTo(2)} className="min-h-[48px] px-6 text-xs tracking-[0.14em] uppercase border border-hair hover:border-ink transition-colors w-full sm:w-auto inline-flex items-center justify-center gap-2" data-testid="stima-back-3"><ArrowLeft size={14} /> Indietro</button>
              <button
                disabled={busy || !form.name.trim() || !form.surname.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email) || !form.phone.trim() || !form.privacy_consent}
                onClick={submit}
                className="btn-ink w-full sm:flex-1 min-h-[48px] disabled:opacity-40 inline-flex items-center justify-center gap-2"
                data-testid="stima-submit"
              >
                <FileText size={15} /> {busy ? "Invio in corso…" : "Scopri la tua stima"}
              </button>
            </div>
            <p className="text-[11px] text-taupe">Campi contrassegnati con * obbligatori. La stima viene calcolata in modo sicuro solo dopo l'invio.</p>
          </div>
        )}

        {/* STEP 4 — La tua stima (visibile solo dopo l'invio valido) */}
        {step === 4 && result && (
          <div className="space-y-6" data-testid="stima-success">
            <div className="bg-white border border-hair p-8 md:p-10 text-center">
              <div className="mx-auto w-12 h-12 border border-gold text-gold flex items-center justify-center"><Check size={22} /></div>
              <h2 className="font-serif text-3xl md:text-4xl mt-6">Grazie, {form.name || "Gentile cliente"}.</h2>
              <p className="mt-2 text-sm text-taupe">Codice della tua richiesta: <strong data-testid="stima-number">{result.number}</strong></p>
              <p className="mt-4 text-taupe leading-relaxed max-w-xl mx-auto text-sm">
                {result.email_sent
                  ? "Ti abbiamo appena inviato la stima via email con il documento PDF allegato. Controlla anche la cartella spam."
                  : "La tua stima è pronta qui sotto; l'invio email è in corso — se non la ricevi, contattaci."}
              </p>
            </div>

            <div className="bg-white border border-hair p-6 md:p-8" data-testid="stima-result-summary">
              <div className="eyebrow mb-5">Riepilogo delle tue scelte</div>
              <dl className="space-y-2 text-sm">
                {[
                  ["Servizio", result.result?.service],
                  result.result?.sqm && ["Metratura", `${result.result.sqm} m²`],
                  ["Tipologia immobile", result.result?.property_type],
                  ["Località", result.result?.comune],
                  ["Urgenza", result.result?.urgency ? "Sì" : "No"],
                ].filter(Boolean).map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-4 border-b border-hair/60 pb-2"><dt className="text-taupe">{k}</dt><dd className="text-right">{v}</dd></div>
                ))}
              </dl>
            </div>

            <div className="bg-white border border-hair p-6 md:p-8" data-testid="stima-result-pricing">
              <div className="eyebrow mb-5">La tua stima preliminare</div>
              {result.manual ? (
                <div>
                  <p className="font-serif text-2xl" data-testid="stima-manual-label">Stima da elaborare su misura</p>
                  <p className="text-sm text-taupe mt-3 leading-relaxed">La superficie o la trasferta richiedono la verifica manuale dello Studio: la tua richiesta avrà priorità "Verifica manuale" e sarai ricontattato personalmente.</p>
                </div>
              ) : (
                <dl className="space-y-2 text-sm">
                  <div className="flex justify-between gap-4 border-b border-hair/60 pb-2"><dt className="text-taupe">Importo base</dt><dd className="font-serif" data-testid="stima-base">{eur(result.result?.pricing?.base)}</dd></div>
                  {result.result?.pricing?.urgency_amount > 0 && <div className="flex justify-between gap-4 border-b border-hair/60 pb-2"><dt className="text-taupe">Maggiorazione urgenza ({result.result.pricing.urgency_percent}%)</dt><dd className="font-serif" data-testid="stima-urgency-amount">{eur(result.result.pricing.urgency_amount)}</dd></div>}
                  <div className="flex justify-between gap-4 border-b border-hair/60 pb-2"><dt className="text-taupe">{result.result?.pricing?.travel_label || "Trasferta"}</dt><dd className="font-serif" data-testid="stima-travel-cost">{eur(result.result?.pricing?.travel_cost)}</dd></div>
                  {result.result?.pricing?.contribution > 0 && <div className="flex justify-between gap-4 border-b border-hair/60 pb-2"><dt className="text-taupe">Contributo previdenziale ({result.result.pricing.contribution_percent}%)</dt><dd className="font-serif" data-testid="stima-contribution">{eur(result.result.pricing.contribution)}</dd></div>}
                  {result.result?.pricing?.vat > 0 && <div className="flex justify-between gap-4 border-b border-hair/60 pb-2"><dt className="text-taupe">IVA ({result.result.pricing.vat_rate}%)</dt><dd className="font-serif" data-testid="stima-vat">{eur(result.result.pricing.vat)}</dd></div>}
                  {result.result?.pricing?.bollo > 0 && <div className="flex justify-between gap-4 border-b border-hair/60 pb-2"><dt className="text-taupe">Imposta di bollo</dt><dd className="font-serif">{eur(result.result.pricing.bollo)}</dd></div>}
                  <div className="flex justify-between gap-4 pt-3 text-lg"><dt>Totale stimato{result.result?.pricing?.amounts_mode === "comprensivi" ? " (comprensivo)" : ""}</dt><dd className="font-serif text-2xl" data-testid="stima-total">{eur(result.result?.pricing?.total)}</dd></div>
                </dl>
              )}
            </div>

            <p className="text-xs text-taupe leading-relaxed border border-hair bg-white p-4" data-testid="stima-disclaimer">
              Questa stima preliminare è <strong className="text-ink">gratuita</strong> e non costituisce un'offerta contrattuale definitiva:
              gli importi indicano il costo stimato del servizio professionale, non il costo della stima.
              L'importo potrà essere confermato o aggiornato dopo l'analisi completa del progetto e delle esigenze.
            </p>

            <div className="flex gap-3 flex-col sm:flex-row">
              {result.pdf_url && (
                <a href={result.pdf_url} target="_blank" rel="noopener" className="btn-ink w-full sm:flex-1 min-h-[48px] inline-flex items-center justify-center gap-2" data-testid="stima-download-pdf">
                  <FileText size={15} /> Scarica il PDF
                </a>
              )}
              <Link to="/prenota" className="min-h-[48px] px-6 w-full sm:flex-1 text-xs tracking-[0.14em] uppercase border border-ink hover:bg-ink hover:text-bone transition-colors inline-flex items-center justify-center gap-2" data-testid="stima-book-free">
                Prenota gratuitamente una consulenza
              </Link>
            </div>
            {result.token && (
              <div className="text-center">
                <Link to={`/stima-conferma/${result.token}`} className="text-xs tracking-[0.14em] uppercase text-taupe hover:text-ink underline" data-testid="stima-confirm-cta">
                  Richiedi la conferma dello Studio
                </Link>
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
